import React, { useCallback } from 'react';

import { link } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { MixedWhiteAlineLogo } from 'src/components/icons/MixedWhiteAlineLogo';
import { SuccessTickLargeIcon } from 'src/components/icons/Success';
import { homePageRoute } from 'src/config';
import { openLink } from 'src/helpers';
import DoneSigningImg from 'src/images/success-page-background.png';
import { makeStyles } from 'tss-react/mui';

import { AboutAline } from './AboutAline';

const usePageStyles = makeStyles()(theme => ({
  body: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh',
    minWidth: theme.breakpoints.values.lg,
    padding: theme.spacing(4),
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      padding: theme.spacing(2),
    },
  },
  container: {
    alignItems: 'center',
    background: theme.palette.common.white,
    borderRadius: 16,
    boxShadow: '0px 5px 35px 1px rgba(0, 0, 0, 0.35)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    padding: theme.spacing(8, 12),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(4),
      padding: theme.spacing(5, 2),
      width: '100%',
    },
  },
  imageSection: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
  },
  img: {
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: -1,
  },
}));

const useCardStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    border: '1px solid #DEDEDE',
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(6),
    position: 'relative',
    width: 500,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 500,
      padding: theme.spacing(5, 2.5, 2.5),
      width: '100%',
    },
  },
  primaryText: {
    fontSize: 24,
    fontWeight: 700,
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
    },
  },
  secondaryText: {
    fontSize: 14,
  },
  successIcon: {
    height: 60,
    position: 'absolute',
    top: '-30px',
    width: 60,
  },
  textContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface SigningOutcomePageProps {
  children: React.ReactNode;
  dataCy: string;
  title: string;
  showAboutAline?: boolean;
}

/**
 * The full-page landing spot for the end of a signer's involvement in a workflow — they finished
 * signing, or they handed it to someone else. Signers who got here from a signature request rather
 * than from Aline itself also see the product pitch.
 */
export function SigningOutcomePage({
  children,
  dataCy,
  showAboutAline = false,
  title,
}: SigningOutcomePageProps): JSX.Element {
  const { classes } = usePageStyles();

  return (
    <>
      <Helmet defaultTitle="Aline" titleTemplate="%s - Aline">
        <title>{title}</title>
      </Helmet>
      <div className={classes.body} data-cy={dataCy}>
        <img className={classes.img} src={DoneSigningImg} srcSet={DoneSigningImg} />
        <div className={classes.imageSection}>
          <MixedWhiteAlineLogo height={32} width={128} />
        </div>
        <div className={classes.container}>
          {children}
          {showAboutAline && <AboutAline />}
        </div>
      </div>
    </>
  );
}

interface SigningOutcomeCardProps {
  children?: React.ReactNode;
  primaryText?: string;
  secondaryText?: string;
}

/**
 * What happened and what the signer can do about it. Copy left undefined means the page is still
 * working out which outcome this is, so the card holds its shape with a skeleton rather than
 * shifting when the text arrives.
 */
export function SigningOutcomeCard({
  children,
  primaryText,
  secondaryText,
}: SigningOutcomeCardProps): JSX.Element {
  const { classes } = useCardStyles();

  return (
    <div className={classes.container}>
      <SuccessTickLargeIcon className={classes.successIcon} />
      <div className={classes.textContainer}>
        <PrimaryText text={primaryText} />
        <SecondaryText text={secondaryText} />
      </div>
      {children}
    </div>
  );
}

/** A signer who arrived by magic link has no Aline app to return to, so send them to the site. */
export function useGoToAline(isInternalUser: boolean): () => void {
  const navigate = useNavigate();

  return useCallback(() => {
    if (isInternalUser) {
      navigate(link.Home());
      return;
    }

    openLink(homePageRoute);
  }, [isInternalUser, navigate]);
}

function PrimaryText({ text }: { text?: string }): JSX.Element {
  const { classes } = useCardStyles();
  if (!text) return <Skeleton height={32} variant="text" width={300} />;

  return (
    <Typography align="center" className={classes.primaryText}>
      {text}
    </Typography>
  );
}

function SecondaryText({ text }: { text?: string }): JSX.Element {
  const { classes } = useCardStyles();
  if (!text) return <Skeleton height={24} variant="text" width={200} />;

  return (
    <Typography align="center" className={classes.secondaryText} color="textSecondary">
      {text}
    </Typography>
  );
}
